import { useFormContext } from 'react-hook-form';
import classNames from 'classnames';
import styles from 'styles/pages/chart/VitalSignChart.module.scss';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import { VitalSignChartFields } from 'constants/chart/vitalSignChart';

type TProps = {
  category: string;
  isRegister: boolean;
  isReadOnly: boolean;
  activeTabIndex: number;
};

const VitalSignChart = ({
  category,
  isRegister,
  isReadOnly,
  activeTabIndex,
}: TProps) => {
  const { register } = useFormContext();

  return (
    <>
      <table className={styles.table}>
        <thead>
          <tr className={styles.thead_tr}>
            <th className={styles.th}></th>
            <th className={styles.th}>생징후</th>
            <th className={styles.th}>정상수치 (성인)</th>
            <th className={styles.th}>1차</th>
            <th className={styles.th}>2차</th>
          </tr>
        </thead>
        <tbody>
          <tr className={styles.tbody_tr}>
            <td
              className={styles.td}
              style={{ color: STYLES_VARIABLES.GRAY_07 }}
            >
              1
            </td>
            <td className={styles.td}>혈압</td>
            <td className={styles.td}>120/80 mmHg</td>
            <td className={styles.td}>
              <div className={styles.input_wrapper}>
                <input
                  {...register(VitalSignChartFields.SysBloodPressure1)}
                  type="number"
                  className={styles.sm_input}
                  placeholder="예) 120"
                  readOnly={
                    !isRegister ||
                    isReadOnly ||
                    (category !== 'practice' && activeTabIndex === 2)
                  }
                />
                <span>/</span>
                <input
                  {...register(VitalSignChartFields.DiasBloodPressure1)}
                  type="number"
                  className={styles.sm_input}
                  placeholder="예) 80"
                  readOnly={
                    !isRegister ||
                    isReadOnly ||
                    (category !== 'practice' && activeTabIndex === 2)
                  }
                />
                <span>mmHg</span>
              </div>
            </td>
            <td className={styles.td}>
              <div className={styles.input_wrapper}>
                <input
                  {...register(VitalSignChartFields.SysBloodPressure2)}
                  type="number"
                  className={styles.sm_input}
                  placeholder="예) 120"
                  readOnly={
                    !isRegister ||
                    isReadOnly ||
                    (category !== 'practice' && activeTabIndex === 0)
                  }
                />
                <span>/</span>
                <input
                  {...register(VitalSignChartFields.DiasBloodPressure2)}
                  type="number"
                  className={styles.sm_input}
                  placeholder="예) 80"
                  readOnly={
                    !isRegister ||
                    isReadOnly ||
                    (category !== 'practice' && activeTabIndex === 0)
                  }
                />
                <span>mmHg</span>
              </div>
            </td>
          </tr>
          <tr className={styles.tbody_tr}>
            <td
              className={styles.td}
              style={{ color: STYLES_VARIABLES.GRAY_07 }}
            >
              2
            </td>
            <td className={styles.td}>맥박</td>
            <td className={styles.td}>60-80회 / min</td>
            <td className={styles.td}>
              <div className={styles.input_wrapper}>
                <input
                  {...register(VitalSignChartFields.Pulse1)}
                  type="number"
                  className={styles.sm_input}
                  placeholder="예) 80"
                  readOnly={
                    !isRegister ||
                    isReadOnly ||
                    (category !== 'practice' && activeTabIndex === 2)
                  }
                />
                <span>회 / min</span>
              </div>
            </td>
            <td className={styles.td}>
              <div className={styles.input_wrapper}>
                <input
                  {...register(VitalSignChartFields.Pulse2)}
                  type="number"
                  className={styles.sm_input}
                  placeholder="예) 80"
                  readOnly={
                    !isRegister ||
                    isReadOnly ||
                    (category !== 'practice' && activeTabIndex === 0)
                  }
                />
                <span>회 / min</span>
              </div>
            </td>
          </tr>
          <tr className={styles.tbody_tr}>
            <td
              className={styles.td}
              style={{ color: STYLES_VARIABLES.GRAY_07 }}
            >
              3
            </td>
            <td className={styles.td}>체온</td>
            <td className={styles.td}>35~37℃</td>
            <td className={styles.td}>
              <div className={styles.input_wrapper}>
                <input
                  {...register(VitalSignChartFields.Temperature1)}
                  type="number"
                  step="0.1"
                  className={styles.sm_input}
                  placeholder="예) 36.5"
                  readOnly={
                    !isRegister ||
                    isReadOnly ||
                    (category !== 'practice' && activeTabIndex === 2)
                  }
                />
                <span>℃</span>
              </div>
            </td>
            <td className={styles.td}>
              <div className={styles.input_wrapper}>
                <input
                  {...register(VitalSignChartFields.Temperature2)}
                  type="number"
                  step="0.1"
                  className={styles.sm_input}
                  placeholder="예) 36.5"
                  readOnly={
                    !isRegister ||
                    isReadOnly ||
                    (category !== 'practice' && activeTabIndex === 0)
                  }
                />
                <span>℃</span>
              </div>
            </td>
          </tr>
          <tr className={styles.tbody_tr}>
            <td
              className={classNames(styles.td, styles.rounded_bottom_left)}
              style={{ color: STYLES_VARIABLES.GRAY_07 }}
            >
              4
            </td>
            <td className={styles.td}>호흡</td>
            <td className={styles.td}>12-16회 / min</td>
            <td className={styles.td}>
              <div className={styles.input_wrapper}>
                <input
                  {...register(VitalSignChartFields.Breath1)}
                  type="number"
                  className={styles.sm_input}
                  placeholder="예) 10"
                  readOnly={
                    !isRegister ||
                    isReadOnly ||
                    (category !== 'practice' && activeTabIndex === 2)
                  }
                />
                <span>회 / min</span>
              </div>
            </td>
            <td className={classNames(styles.td, styles.rounded_bottom_right)}>
              <div className={styles.input_wrapper}>
                <input
                  {...register(VitalSignChartFields.Breath2)}
                  type="number"
                  className={styles.sm_input}
                  placeholder="예) 10"
                  readOnly={
                    !isRegister ||
                    isReadOnly ||
                    (category !== 'practice' && activeTabIndex === 0)
                  }
                />
                <span>회 / min</span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

export default VitalSignChart;
